import * as UI from "@iyk/ui"
import Hls from "hls.js"
import * as React from "react"

export const HlsVideo = ({ src, autoPlay, ref, ...props }: HlsVideo.Props) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const { setVideoSrc, destroyHls, controlPlayPause } = useHlsVideo(videoRef)

  React.useEffect(() => {
    if (videoRef.current && src) {
      setVideoSrc(src)
    }

    return () => {
      destroyHls()
    }
  }, [src])

  React.useEffect(() => {
    if (autoPlay && videoRef.current) {
      controlPlayPause(true)
    } else if (!autoPlay && videoRef.current) {
      controlPlayPause(false)
    }
  }, [autoPlay])

  return <video ref={UI.extendRef(videoRef, ref)} {...props} />
}

const useHlsVideo = (videoRef: React.RefObject<HTMLVideoElement | null>) => {
  const hlsRef = React.useRef<Hls | null>(null)

  const setVideoSrc = (videoSrc: string) => {
    if (!videoRef.current) return

    if (videoRef.current.canPlayType?.("application/vnd.apple.mpegurl")) {
      videoRef.current.src = videoSrc
    } else if (Hls.isSupported()) {
      hlsRef.current = new Hls({ manifestLoadingMaxRetry: 8 })
      hlsRef.current.loadSource(videoSrc)

      if (videoRef.current) {
        hlsRef.current.attachMedia(videoRef.current)
      }
    }
  }

  const controlPlayPause = (shouldPlay: boolean) => {
    if (videoRef.current) {
      if (shouldPlay) {
        videoRef.current.play().catch(console.error)
      } else {
        videoRef.current.pause()
      }
    }
  }

  return {
    setVideoSrc,
    destroyHls: () => hlsRef.current?.destroy(),
    controlPlayPause,
  }
}

export namespace HlsVideo {
  export type Props = React.ComponentProps<"video">
}
